import { Role } from 'auth';
import { AxiosError } from 'axios';
import cloneDeep from 'lodash/cloneDeep';
import get from 'lodash/get';
import isEqual from 'lodash/isEqual';
import { ChangeEvent, useCallback, useContext, useMemo, useRef, useState } from 'react';
import { FormBuilderPostData, ReactFormBuilder, TaskData } from 'react-form-builder';
import toast from 'react-hot-toast';
import { Navigate, useLocation, useNavigate, useParams } from 'react-router-dom';
import { withPrivateAccess } from '@/modules/auth/hocs/withPrivateAccess.tsx';
import { useFetchAllRegisters } from '@/modules/common/hooks/useFetchAllRegisters.ts';
import { useFormQuery } from '@/modules/common/queries/forms.ts';
import { commonPaths } from '@/modules/common/routes.tsx';
import CreateRegisterModal from '@/modules/admin-dashboard/components/modals/CreateRegisterModal.tsx';
import FormPreviewModal from '@/modules/admin-dashboard/components/modals/FormPreviewModal.tsx';
import { useUpdateFormMutation } from '@/modules/admin-dashboard/queries/forms.ts';
import { useCreateRegisterMutation } from '@/modules/admin-dashboard/queries/registers.ts';
import { adminDashboardPaths } from '@/modules/admin-dashboard/routes.tsx';
import { Button, ModalProviderContext, PageLayout, Spinner, TextInput } from 'ui';
import { usePrompt } from 'ui/hooks';
import { CreateRegisterResponse, GetFormResponse } from 'compliance-api';

const formValidator = (data: TaskData[]) => {
  return data.every(({ compliance, registerId }) => !compliance || (compliance && registerId));
};

const Form = () => {
  const { id } = useParams();
  const [taskData, setTaskData] = useState<TaskData[]>([]);
  const { state } = useLocation();
  const formBuilderRef = useRef<{ validate: () => Promise<boolean> }>(null);

  const handleFormLoaded = useCallback((data: GetFormResponse) => {
    setTaskData(get(data, 'forms.data', []) as TaskData[]);
    setName(data.forms.name);
  }, []);

  const { data, isLoading, isFetched, error } = useFormQuery(+(id || '0'), {
    onSuccess: handleFormLoaded,
  });

  const [name, setName] = useState(data?.forms?.name || '');
  const { openModal } = useContext(ModalProviderContext);
  const navigate = useNavigate();

  const { registers } = useFetchAllRegisters();

  const { mutateAsync: updateForm, isLoading: isUpdating } = useUpdateFormMutation();
  const { isLoading: isRegisterCreation } = useCreateRegisterMutation();

  const form = data?.forms;

  const initialTaskData = useMemo(() => get(data, 'forms.data', []), [data]);

  const initialName = form?.name || '';

  const isChanged = !isEqual(taskData, initialTaskData) || initialName !== name;

  const handleExit = useCallback(() => {
    const isConfirmed = !isChanged || confirm('Your changes will not be saved. Continue?');

    if (isConfirmed) {
      navigate({
        pathname: get(state, 'returnUrl', adminDashboardPaths.static.forms),
        search: location.search,
      });
    }
  }, [isChanged, navigate, state]);

  const handleSaveForm = useCallback(async () => {
    const isValid = await formBuilderRef?.current?.validate();
    if (isValid) {
      await updateForm({ id: +(id || '0'), data: taskData, name });
      navigate({
        pathname: get(state, 'returnUrl', adminDashboardPaths.static.forms),
        search: location.search,
      });
    } else {
      toast('Some form elements contain invalid data');
    }
  }, [updateForm, id, taskData, name, navigate, state]);

  const handleFormsChanged = useCallback((data: FormBuilderPostData) => {
    setTaskData(data);
  }, []);

  const handleChangeName = useCallback((e: ChangeEvent<HTMLInputElement>) => {
    if (e.target.value.length < 101) {
      setName(e.target.value);
    }
  }, []);

  const handleOpenPreviewModal = useCallback(() => {
    openModal(FormPreviewModal, {
      title: name,
      taskData: cloneDeep(taskData),
      registers,
    });
  }, [name, openModal, registers, taskData]);

  const handleCreateRegister = useCallback<() => Promise<{ id: number; name: string }>>(
    () =>
      new Promise(resolve => {
        const onSuccess = async (res: CreateRegisterResponse) => {
          resolve({ id: res.response.id, name: res.response.name });
        };

        openModal(CreateRegisterModal, { onSuccess, wrapperClasses: { dialog: '!z-[5]' } });
      }),
    [openModal],
  );

  const isPossibleSave =
    formValidator(taskData) && isChanged && !isLoading && !isUpdating && name.trim();

  const headerAction = (
    <div className="flex gap-4 w-[450px] flex-wrap justify-end">
      <Button
        variant="secondary"
        disabled={isLoading || isUpdating || !taskData.length}
        onClick={handleOpenPreviewModal}
        className="w-auto"
      >
        Preview
      </Button>
      <Button
        variant="secondary"
        disabled={isLoading || isUpdating}
        onClick={handleExit}
        className="w-auto"
      >
        Exit without saving
      </Button>
      <Button
        variant="primary"
        loading={isLoading || isUpdating}
        disabled={!isPossibleSave}
        onClick={handleSaveForm}
        className="w-auto"
      >
        Save & Exit
      </Button>
    </div>
  );

  usePrompt(isChanged);

  if (
    (error && error instanceof AxiosError && error?.response?.status === 404) ||
    !id ||
    isNaN(+id)
  ) {
    return <Navigate to={commonPaths.static.notFound} />;
  }

  return (
    <PageLayout
      title={
        <div className="flex items-center">
          <TextInput
            name="title"
            className="text-2xl font-semibold !border-0 text-ellipsis"
            value={name}
            placeholder="Enter form name..."
            onChange={handleChangeName}
            disabled={isLoading || isUpdating}
          />
          <div className="text-gray-500 font-sans whitespace-nowrap">
            <strong>{form?.reference_name}</strong>
          </div>
        </div>
      }
      headerAction={headerAction}
      contentClassName="!p-0"
      classNameTitle="[&>div:first-child]:flex-1"
    >
      {(!isFetched || isLoading) && (
        <div className="w-full h-full flex justify-center items-center">
          <Spinner size="lg" />
        </div>
      )}

      <style>
        {`
          .react-form-builder-toolbar  li {
            background-color: #F5F5F5 !important;
            border: none !important;
            border-radius: 4px !important;
            color: #676767 !important;
          }

          .react-form-builder-toolbar {
            margin-top: 0;
          }

          .react-form-builder-toolbar > h4 {
            display: none:
          }

          .tooltip {
            opacity: 0;
          }

          .react-form-builder .react-form-builder-preview {
            aspect-ratio: 1 / 1.414;
            overflow: auto;
          }

          canvas {
            border: 1px solid #BBB;
            border-radius: 4px;
          }
          `}
      </style>
      {isFetched && !isLoading && (
        <ReactFormBuilder
          imperativeRef={formBuilderRef}
          registers={registers}
          data={cloneDeep(initialTaskData) as TaskData[]}
          onChange={handleFormsChanged}
          onCreateRegister={handleCreateRegister}
          isRegisterCreationLoading={isRegisterCreation}
        />
      )}
    </PageLayout>
  );
};

export default withPrivateAccess(Form, Role.COMPLIANCE_ADMIN);
